import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

const areas = [
  {
    name: 'Monroe County, MI',
    desc: 'River towns, lake communities, and suburban neighborhoods.',
    img: 'https://public.readdy.ai/ai/img_res/c75ad31d57a699105dd3c0f439aead99.jpg',
  },
  {
    name: 'Lenawee County, MI',
    desc: 'Quiet rural living and close-knit small towns.',
    img: 'https://readdy.ai/api/search-image?query=lenawee%20county%20michigan%20rural%20farmland%20countryside%20scenic%20rolling%20hills%20small%20town%20homes%20pastoral%20midwest%20landscape%20sunny%20day&width=600&height=400&seq=area-lenawee&orientation=landscape',
  },
  {
    name: 'Washtenaw County, MI',
    desc: 'Ann Arbor area homes with access to top schools.',
    img: 'https://readdy.ai/api/search-image?query=ann%20arbor%20michigan%20charming%20residential%20street%20tree%20lined%20houses%20university%20town%20upscale%20neighborhood%20fall%20leaves%20warm%20sunlight&width=600&height=400&seq=area-washtenaw&orientation=landscape',
  },
  {
    name: 'Wayne County, MI',
    desc: 'Metro Detroit communities with great value and access.',
    img: 'https://readdy.ai/api/search-image?query=wayne%20county%20michigan%20suburban%20homes%20detroit%20metro%20area%20well%20maintained%20neighborhood%20clean%20streets%20residential%20community%20bright%20day&width=600&height=400&seq=area-wayne&orientation=landscape',
  },
  {
    name: 'Lucas County, OH',
    desc: 'Toledo and surrounding communities just over the border.',
    img: 'https://readdy.ai/api/search-image?query=toledo%20ohio%20lucas%20county%20suburban%20residential%20neighborhood%20brick%20homes%20clean%20streets%20midwest%20community%20pleasant%20sunny%20afternoon&width=600&height=400&seq=area-lucas&orientation=landscape',
  },
  {
    name: 'Jackson County, MI',
    desc: 'Affordable homes and land in a growing market.',
    img: 'https://readdy.ai/api/search-image?query=jackson%20michigan%20residential%20neighborhood%20mixed%20housing%20affordable%20homes%20green%20lawns%20midwest%20suburban%20pleasant%20community%20sunlight&width=600&height=400&seq=area-jackson&orientation=landscape',
  },
];

export default function HomeNeighborhoods() {
  const headingRef = useReveal(0);
  const card0 = useReveal(0);
  const card1 = useReveal(80);
  const card2 = useReveal(160);
  const card3 = useReveal(0);
  const card4 = useReveal(80);
  const card5 = useReveal(160);
  const cardRefs = [card0, card1, card2, card3, card4, card5];
  const ctaRef = useReveal(0);

  return (
    <section className="py-14 sm:py-20 bg-[#FAF9F7]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div ref={headingRef} className="reveal flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-12 gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-3 font-medium">Where I Work</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111] leading-tight">
              Neighborhood <em className="not-italic font-semibold">Guides</em>
            </h2>
          </div>
          <Link
            to="/areas"
            className="text-sm font-semibold text-[#1C3A6B] hover:text-[#C8923A] inline-flex items-center gap-2 transition-colors whitespace-nowrap group"
          >
            View All Areas
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {areas.map((area, i) => (
            <div
              key={i}
              ref={cardRefs[i]}
              className="reveal scale group relative overflow-hidden rounded-lg cursor-pointer hover-lift"
            >
              <div className="relative h-56">
                <img
                  src={area.img}
                  alt={area.name}
                  className="w-full h-full object-cover object-top transition-transform duration-600 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-cormorant text-xl font-semibold text-white mb-1">{area.name}</h3>
                  <p className="text-white/70 text-xs">{area.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div ref={ctaRef} className="reveal text-center mt-10">
          <p className="text-sm text-[#888] mb-4">Not sure if I serve your area? Just ask — I'll always point you in the right direction.</p>
          <Link
            to="/areas"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#1C3A6B] text-[#1C3A6B] text-sm font-semibold rounded-md hover:bg-[#1C3A6B] hover:text-white transition-all whitespace-nowrap"
          >
            Explore All Areas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
